import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { OnboardingContext } from './onboardingGuideContext.js'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from './AuthContext'
import { getOrientationContent } from '../lib/onboarding/content/getOrientationContent'
import { getSurfaceGuideContent } from '../lib/onboarding/content/getSurfaceGuideContent'
import {
  getOnboardingRole,
  getRoleHomePath,
  isDashboardPath,
  isSurfaceAvailableForUser,
  shouldRedirectForPendingOrientation,
} from '../lib/onboarding/onboardingSurfaces'
import api from '../lib/api'
import {
  clearOrientationState,
  hydrateOnboardingGuidesFromServer,
  isSurfaceSeen,
  markOrientationComplete,
  markSurfaceSeen,
  shouldShowOrientation,
  snoozeOrientation,
} from '../lib/onboarding/onboardingStorage'
import { ProductGuideDialog } from '../components/onboarding/ProductGuideDialog'
import { FeatureGuideDialog } from '../components/onboarding/FeatureGuideDialog'
import { blurFocusOutsideDialogs } from '../lib/blurFocusOutsideDialogs'

export function OnboardingProvider({ children }) {
  const { userProfile } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const role = userProfile?.role
  const userId = userProfile?.id
  const tenantId = userProfile?.tenant_id

  const [orientationOverride, setOrientationOverride] = useState(null)
  const [surfaceGuideId, setSurfaceGuideId] = useState(null)
  const hydratedGuidesRef = useRef(null)

  const persistOnboardingPatch = useCallback(async (patch) => {
    try {
      await api.auth.patchOnboardingGuides(patch)
    } catch (err) {
      console.warn('Failed to persist onboarding guides to server', err)
    }
  }, [])

  useEffect(() => {
    if (!userId || !userProfile?.onboarding_guides_state) return
    const stateKey = JSON.stringify(userProfile.onboarding_guides_state)
    if (hydratedGuidesRef.current === stateKey) return
    hydratedGuidesRef.current = stateKey
    hydrateOnboardingGuidesFromServer(userProfile.onboarding_guides_state, userId, tenantId)
  }, [userId, userProfile?.onboarding_guides_state, tenantId])

  const orientationContent = useMemo(
    () => (role ? getOrientationContent(role, userProfile) : null),
    [role, userProfile],
  )

  const onboardingAvailable = Boolean(getOnboardingRole(role, tenantId) && orientationContent)

  const surfaceGuideContent = useMemo(
    () => (surfaceGuideId ? getSurfaceGuideContent(surfaceGuideId) : null),
    [surfaceGuideId],
  )

  const autoShowOrientation = useMemo(() => {
    if (!onboardingAvailable || !userId || !role) return false
    return (
      shouldShowOrientation(userId, role, tenantId) &&
      isDashboardPath(location.pathname, role)
    )
  }, [onboardingAvailable, userId, role, tenantId, location.pathname])

  const orientationOpen = orientationOverride ?? autoShowOrientation

  useLayoutEffect(() => {
    if (orientationOpen || surfaceGuideId) {
      blurFocusOutsideDialogs()
    }
  }, [orientationOpen, surfaceGuideId])

  useEffect(() => {
    if (!onboardingAvailable || !userId || !role) return
    const pending = shouldShowOrientation(userId, role, tenantId)
    if (
      !shouldRedirectForPendingOrientation({
        pathname: location.pathname,
        role,
        shouldShowOrientation: pending,
        isDashboard: isDashboardPath(location.pathname, role),
      })
    ) {
      return
    }
    navigate(getRoleHomePath(role), { replace: true })
  }, [
    onboardingAvailable,
    userId,
    role,
    tenantId,
    location.pathname,
    navigate,
  ])

  const handleOrientationSnooze = useCallback(() => {
    if (userId && role) {
      snoozeOrientation(userId, role, undefined, tenantId)
      persistOnboardingPatch({
        action: 'snooze_orientation',
        role,
        tenant_id: tenantId ?? undefined,
      })
    }
    setOrientationOverride(false)
  }, [userId, role, tenantId, persistOnboardingPatch])

  const handleOrientationComplete = useCallback(() => {
    if (userId && role) {
      markOrientationComplete(userId, role, tenantId)
      persistOnboardingPatch({
        action: 'mark_orientation_complete',
        role,
        tenant_id: tenantId ?? undefined,
      })
    }
    setOrientationOverride(false)
  }, [userId, role, tenantId, persistOnboardingPatch])

  const isSurfaceGuideAvailable = useCallback(
    (surfaceId) => {
      if (!surfaceId || !role) return false
      return isSurfaceAvailableForUser(surfaceId, role, tenantId)
    },
    [role, tenantId],
  )

  const openSurfaceGuide = useCallback(
    (surfaceId) => {
      if (!isSurfaceGuideAvailable(surfaceId)) return
      blurFocusOutsideDialogs()
      setSurfaceGuideId(surfaceId)
    },
    [isSurfaceGuideAvailable],
  )

  const openSurfaceGuideOnce = useCallback(
    (surfaceId) => {
      if (!userId || !isSurfaceGuideAvailable(surfaceId)) return
      if (isSurfaceSeen(userId, surfaceId)) return
      blurFocusOutsideDialogs()
      setSurfaceGuideId(surfaceId)
    },
    [userId, isSurfaceGuideAvailable],
  )

  const markSurfaceSeenAndPersist = useCallback(
    (surfaceId) => {
      if (!userId || !surfaceId) return
      markSurfaceSeen(userId, surfaceId)
      persistOnboardingPatch({ action: 'mark_surface_seen', surface_id: surfaceId })
    },
    [userId, persistOnboardingPatch],
  )

  const handleSurfaceGuideClose = useCallback(() => {
    if (surfaceGuideId) markSurfaceSeenAndPersist(surfaceGuideId)
    setSurfaceGuideId(null)
  }, [surfaceGuideId, markSurfaceSeenAndPersist])

  const handleSurfaceGuideComplete = useCallback(() => {
    if (surfaceGuideId) markSurfaceSeenAndPersist(surfaceGuideId)
    setSurfaceGuideId(null)
  }, [surfaceGuideId, markSurfaceSeenAndPersist])

  const replayOrientation = useCallback(() => {
    if (!userId || !role) return
    blurFocusOutsideDialogs()
    clearOrientationState(userId, role, tenantId)
    setOrientationOverride(true)
  }, [userId, role, tenantId])

  const value = useMemo(
    () => ({
      onboardingAvailable,
      replayOrientation,
      openSurfaceGuide,
      openSurfaceGuideOnce,
      isSurfaceGuideAvailable,
    }),
    [
      onboardingAvailable,
      replayOrientation,
      openSurfaceGuide,
      openSurfaceGuideOnce,
      isSurfaceGuideAvailable,
    ],
  )

  return (
    <OnboardingContext.Provider value={value}>
      {children}
      {orientationContent ? (
        <ProductGuideDialog
          key={`orientation-${orientationOpen}`}
          open={orientationOpen}
          content={orientationContent}
          onSnooze={handleOrientationSnooze}
          onComplete={handleOrientationComplete}
        />
      ) : null}
      {surfaceGuideContent ? (
        <FeatureGuideDialog
          key={surfaceGuideId ?? 'surface-idle'}
          open={Boolean(surfaceGuideId)}
          content={surfaceGuideContent}
          onClose={handleSurfaceGuideClose}
          onComplete={handleSurfaceGuideComplete}
        />
      ) : null}
    </OnboardingContext.Provider>
  )
}
